import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useMemo, useEffect, useState, CSSProperties, ReactNode } from 'react'
import { Palette } from 'src/feature/theme/model/Palette'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'

type TourTip = {
  selector: string
  content: string | ReactNode
  position?: 'top' | 'right' | 'bottom' | 'left' | 'center' | [number, number]
  styles?: {
    maskArea?: (base: CSSProperties) => CSSProperties // For last step not to point to an element
  }
}

export const useOverlayTourTips = (): Record<string, TourTip> => {
  const { translation, isLocale } = useLocalizationStore()
  const [isReady, setIsReady] = useState(false)

  // Wait till translations are ready before building tour steps
  useEffect(() => {
    setIsReady(Boolean(translation?.tourSteps))
  }, [translation])

  return useMemo(() => {
    // Return empty object if locale is not set, translations are missing, or not ready yet
    if (!isLocale || !translation?.tourSteps || !isReady) {
      return {}
    }

    const steps: Record<string, TourTip> = {
      company: {
        selector: '.company',
        content: (
          <AppBox style={{ whiteSpace: 'pre-line' }}>
            {translation.tourSteps.company.content()}
          </AppBox>
        ),
      },
      myDashboard: {
        selector: '.myDashboard',
        content: translation.tourSteps.myDashboard.content(),
      },
      myGrowth: {
        selector: '.myGrowth',
        content: translation.tourSteps.myGrowth.content(),
      },
      eLearningV2: {
        selector: '.eLearningV2',
        content: translation.tourSteps.eLearningV2.content(),
      },
      skillGap: {
        selector: '.skillGap',
        content: translation.tourSteps.skillGap.content(),
      },
      projectBuilder: {
        selector: '.projectBuilder',
        content: translation.tourSteps.projectBuilder.content(),
      },
      controlpanel: {
        selector: '.controlpanel',
        content: translation.tourSteps.controlpanel.content(),
      },
      contactCard: {
        selector: '.contactCard',
        content: (
          <>
            {translation.tourSteps.contactCard.content()}{' '}
            <a
              href="mailto:hello@yohrs.com"
              style={{
                color: Palette.GOLDEN_ORANGE,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none'
              }}
            >
              hello@yohrs.com
            </a>
          </>
        ),
        position: 'center',
        styles: {
          maskArea: (base: CSSProperties) => ({ ...base, display: 'none' }),
        },
      },
    }

    // Only return steps if all steps have valid selector and content
    const allValid = Object.values(steps).every(
      step => step.selector?.trim() && (typeof step.content === 'string' ? step.content?.trim() : step.content),
    )

    return allValid ? steps : {}
  }, [translation, isLocale, isReady])
}